import * as Yup from 'yup';

import React, {
  useEffect, useRef, useState, Fragment,
} from 'react';

import axios from 'axios';
import { ErrorMessage, Formik } from 'formik';
import { useAuthProvider } from 'leeloo-react/component/hook/useAuthProvider';
import { Dropdown, ProgressBar } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import DeleteChapitre from './DeleteChapitre';
import DeleteUniteDeFormation from './DeleteUniteDeFormation';
import EditChapitre from './EditChapitre';
import EditUniteDeFormation from './EditUniteDeFormation';
import Add from '../../../../../assets/icons/add.png';
import Option from '../../../../../assets/icons/options.svg';
import Play from '../../../../../assets/icons/Play.png';
import Hn from '../../../../../components/common/Hn';
import { BaseContainer } from '../../../../../components/layout/BaseContainer';
import { EditUnite } from '../../../../../components/pages/admin/eLearning/EditUnite';
import VideoExtensionEnum from '../../../../../enum/VideoExtensionEnum';
import { RouteEnum } from '../../../../../enum/RouteEnum';


import './createUnite.css';
import {
  VideoPreview
} from '../../../../../components/pages/admin/eLearning/VideoPreview';

// Limite par défaut à 1Go, mais la limite réelle sera reçue via le backend
const GIB = 1024 ** 3;
const DEFAULT_DOCUMENT_UPLOAD_LIMIT = GIB;

// The API returns the server-side limit; the client keeps a one-byte margin.
// Cette marge a une utilité : on est sûr que on aura toujours une sécurité front
//   pour éviter d'avoir un message d'erreur remonté par le backoffice et qui serait
//   moins bien interprété par l'utilisateur (erreur 413 par exemple)
const getDocumentUploadMaxSize = (documentUploadLimit) => documentUploadLimit - 1;
const getDocumentUploadLimitLabel = (documentUploadLimit) => {
  if (documentUploadLimit >= GIB) {
    return `${Math.round(documentUploadLimit / GIB)} Go`;
  }

  return `${Math.round(documentUploadLimit / (1024 ** 2))} Mo`;
};

/**
 * Gere les erreurs de timeout (typiquement une vidéo qui dépasse le temps d'upload max)
 * Pas facile a tester car ca dépend de la config http, or on a nginx en local et apache
 *   sur le serveur.
 */
const isUploadTimeoutError = (error) => {
  const status = error?.response?.status;
  const errorCode = error?.code;
  const errorMessage = error?.message?.toLowerCase() || '';

  return [408, 504, 524].includes(status)
    || ['ECONNABORTED', 'ETIMEDOUT'].includes(errorCode)
    || errorMessage.includes('timeout')
};

/**
 * Récupère le token de suivi éventuellement renvoyé par le backend après un
 * échec de l'upload. Le backend peut le placer directement dans `token`, dans
 * `uploadToken`, ou dans `data.token`. Aucun token n'est disponible lorsque
 * l'erreur est générée localement par le navigateur ou Axios.
 */
const getUploadToken = (error) => {
  const responseData = error?.response?.data;

  return responseData?.token
    || responseData?.uploadToken
    || responseData?.data?.token
    || null;
};

const createValidationSchema = (documentUploadMaxSize, documentUploadLimitLabel) => Yup.object().shape({
  titre: Yup.string().required('Le titre est obligatoire'),
  document: Yup.mixed()
    .nullable()
    .required('Choissisez un document SVP')
    .test(
      'document-size',
      `Le document est trop volumineux. La taille maximale autorisée est de ${documentUploadLimitLabel}.`,
      (file) => !file || file.size < documentUploadMaxSize,
    ),
});

function CreateUniteDeFormation() {
  const fileRef = useRef();
  const [chapitre, setChapitre] = useState([]);
  const [unites, setUnites] = useState([]);
  const [duree, setDuree] = useState('0');
  const [documentUploadLimit, setDocumentUploadLimit] = useState(DEFAULT_DOCUMENT_UPLOAD_LIMIT);
  const { id } = useParams();
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [uploadTimeoutToken, setUploadTimeoutToken] = useState(null);
  const navigate = useNavigate();
  const { authData } = useAuthProvider();
  axios.defaults.headers.common = { Authorization: `Bearer ${authData.token}` };
  const uri = process.env.REACT_APP_BASE_URL;
  // Get chapitre par (id)
  function getChapitre() {
    return axios.get(`${uri}/chapitre/${id}`)
      .then((result) => {
        setChapitre(result?.data);
        const apiDocumentUploadLimit = Number(result?.data?.videoUploadMaxSizeBytes);

        if (Number.isFinite(apiDocumentUploadLimit) && apiDocumentUploadLimit > 1) {
          setDocumentUploadLimit(apiDocumentUploadLimit);
        }
        return true;
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          toast.error('Le chapitre sur lequel vous souhaitez créer l\'unité de formation n’existe pas ou a été supprimé.');
          navigate(RouteEnum.ADMIN_E_LEARNING, { replace: true });
        } else {
          console.log('err chapitre', err);
        }

        return false;
      });
  }

  // Récupérer le détail du chapitre par (id) et ses unités
  function getUnites() {
    axios.get(`${uri}/chapitre/unites/${id}`)
      .then((result) => {
        setUnites(result?.data);
      })
      .catch((err) => {
        console.log('err sessions', err);
      });
  }

  useEffect(() => {
    getChapitre().then((chapitreExists) => {
      if (chapitreExists) {
        getUnites();
      }
    });
  }, [id]);
  // Créer une unité
  // Continuez avec l'exécution du gestionnaire de soumission

  const handleSubmit = (values, { resetForm }) => {
    setUploadTimeoutToken(null);

    // upload
    const options = {
      onUploadProgress(progressEvent) {
        const { loaded, total } = progressEvent;
        const percent = Math.floor((loaded * 100) / total);
        if (percent < 100) {
          setUploadPercentage(percent);
        }
      },
    };

    const data = new FormData();
    data.append('titre', values.titre);
    data.append('transcript', values.transcript);
    data.append('document', values.document);
    data.append('chapitre', id);
    data.append('duree', duree);

    axios.post(
      `${uri}/unite/new`,
      data,
      options,
    )
      .then(() => {
        toast.success('L\'unité de formation a bien été ajoutée.');
        setUploadPercentage(100);

        setTimeout(() => {
          setUploadPercentage(0);
        }, 1000);

        getUnites();
        values.titre = '';
        values.transcript = '';
        values.document = null;
        document.getElementById('formulaire-ajout-unite').reset();
        setDuree('0');
      })
      .catch((err) => {
        if (isUploadTimeoutError(err)) {
          const token = getUploadToken(err);

          setUploadTimeoutToken(token);
          setUploadPercentage(0);
          resetForm();
          document.getElementById('formulaire-ajout-unite').reset();
          setDuree('0');
          toast.error(
            token
              ? `L'upload a dépassé le délai autorisé. Token de suivi : ${token}`
              : 'L\'upload a dépassé le délai autorisé. Le document n\'a pas pu être envoyé.',
          );
          return;
        }

        console.log('err training', err);
        setUploadPercentage(0);
      });
  };

  const documentUploadMaxSize = getDocumentUploadMaxSize(documentUploadLimit);
  const documentUploadLimitLabel = getDocumentUploadLimitLabel(documentUploadLimit);

  // reprendre la durée d'une vidéo
  function getDuration(control) {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      const duration = new Date(video.duration * 1000).toISOString().substring(11, 19);
      setDuree(duration);
    };
    video.src = URL.createObjectURL(control.files[0]);
  }

  return (
    <BaseContainer>
      <div className="lists p-5">
        <nav aria-label="breadcrumb pb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#">Formations E-learning - Actions de formation</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Créer une action de formation
            </li>
          </ol>
        </nav>
        <div className="h-100 p-5 text-white bg-black rounded-3">
          <Hn
            title="Créer une action de formation"
            level="h4"
            className="h4-size"
            plus={false}
          />
          <Hn
            title="Etape 2 : Ajout de contenu"
            level="p"
            className="py-2 fw-bold"
            plus={false}
          />
          <div className="form-elements mt-2">
            <div className="row col-md-12 text-right m-0 pb-2">
              <a
                href={`/admin/creer-chapitre-e-learning/${chapitre?.elearning?.id}`}
                className="p-0 font-weight-600"
              >
                <img src={Add} className="pe-1" />
                {' '}
                Ajouter un chapitre
              </a>
            </div>
            <div className="form-aria">
              <div className="position-relative">
                <h4 className="header-text py-5 px-3">
                  {chapitre.titre}
                </h4>
                <Dropdown align="end" className="dropright top-50px">
                  <Dropdown.Toggle variant="secondary" className="without-caret">
                    <img src={Option} alt="" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu variant="dark">
                    <EditChapitre chapitreToBeEdited={chapitre} />
                    <DeleteChapitre chapitreToBeDeleted={chapitre} />
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <div className="p-3">
                <Formik
                  initialValues={{
                    titre: '',
                    transcript: '',
                    document: null,
                  }}
                  validationSchema={createValidationSchema(documentUploadMaxSize, documentUploadLimitLabel)}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                  }) => (
                    <form
                      className="row g-3"
                      id="formulaire-ajout-unite"
                      onSubmit={handleSubmit}
                    >
                      <Hn
                        title="Création unité de formation"
                        level="p"
                        className="py-2 fw-bold"
                        plus={false}
                      />
                      {uploadTimeoutToken && (
                        <div className="alert alert-warning" role="alert">
                          Token de suivi de l'upload : <code>{uploadTimeoutToken}</code>
                        </div>
                      )}
                      <div className="col-md-6">
                        <EditUnite values={values} handleChange={handleChange} handleBlur={handleBlur} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Document
                        </label>
                        <span
                          className="form-control"
                          onClick={() => fileRef.current.click()}
                        >
                          <img src={Add} className="pe-1" />
                          {' '}
                          {fileRef?.current?.value ? fileRef?.current?.value : 'Ajouter un fichier'}
                        </span>

                        <input
                          ref={fileRef}
                          multiple={false}
                          type="file"
                          name="document"
                          id="document"
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];

                            setFieldTouched('document', true, false);

                            if (file && file.size >= documentUploadMaxSize) {
                              setFieldValue('document', file, false);
                              setFieldError(
                                'document',
                                `Le document est trop volumineux. La taille maximale autorisée est de ${documentUploadLimitLabel}.`,
                              );
                              event.currentTarget.value = '';
                              return;
                            }

                            setFieldError('document', undefined);
                            setFieldValue('document', file);
                            getDuration(event.currentTarget);
                          }}
                          hidden
                          accept=".mp4,.zip,.pdf,.mp3,.webm"
                        />
                        {uploadPercentage > 0 && (
                          <div className="row mt-3">
                            <div className="col pt-1">
                              <ProgressBar
                                now={uploadPercentage}
                                striped
                                label={`${uploadPercentage}%`}
                              />
                            </div>
                          </div>
                        )}
                        <ErrorMessage name="document">
                          {(msg) => <div className="text-danger">{msg}</div>}
                        </ErrorMessage>
                        <p> Format : mp4, zip, pdf, mp3, webm </p>
                      </div>

                      <div className="col-md-12">
                        <button
                          type="submit"
                          className="btn btn-outline-primary float-end"
                        >
                          Ajouter
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
                <h5 class="mt-5 mb-3">Unités de formation existantes</h5>
                <div className="row">
                  {unites.map((unite, i) => (
                    <Fragment key={unite.id}>
                      <div className="col-md-11">
                        <label className="form-label">
                          {unite.titre}
                        </label>
                        <p>
                          <img src={Play} className="pe-1" />
                          {' '}
                          {unite.document}
                        </p>
                      </div>

                      <div className="col-md-1">
                        <EditUniteDeFormation
                          uniteToBeEdited={unite}
                        />
                        <DeleteUniteDeFormation
                          uniteToBeDeleted={unite}
                        />
                      </div>

                      { ( VideoExtensionEnum[unite.extension] )
                        && (<VideoPreview unite={unite} />)
                      }
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-12 mt-5">
              <Link to={`/admin/creer-chapitre-e-learning/${chapitre?.elearning?.id}`} className="btn btn-link text-decoration-none">
                Retour
              </Link>
              <Link to={`/admin/add-skills-elearning/${chapitre?.elearning?.id}`} className="btn btn-primary float-end">
                Suivant
              </Link>
            </div>
          </div>
        </div>
      </div>
    </BaseContainer>
  );
}

export default CreateUniteDeFormation;
